import { RelayRestartControl } from './RelayRestartControl';
import { useState, useSyncExternalStore } from 'react';
import { Key, ArrowUpCircle, WifiLow, VolumeX } from 'lucide-react';
import { useMarketStore } from '@/stores/marketStore';
import { useFeedFreshness, type FeedStatus } from '@/hooks/useFeedFreshness';
import { useRateLimitStatus, bumpRateLimitStatus, type RateLimitStatus } from '@/hooks/useRateLimitStatus';
import { useGatewayHealth } from '@/hooks/useGatewayHealth';
import { jsHeapUsedMb } from '@/lib/infra/browserDiagnostics';
import { FLUSH_P95_WARN_MS, QUEUE_LAG_WARN_MS } from '@/lib/feed/gatewayHealthMeter';
import { useAuthStore, apiFetch } from '@/stores/authStore';
import { useLowBandwidth } from '@/stores/lowBandwidth';
import { useBuildUpdateStore } from '@/stores/buildUpdateStore';
import { OddsFeeToggles } from '@/components/molecules/OddsFeeToggles';
import { RestingOrderDisplayToggle } from '@/components/molecules/RestingOrderDisplayToggle';
import { OrderbooksPausedBadge } from '@/components/atoms/OrderbooksPausedBadge';
import { VenueCredStatus } from '@/components/VenueCredStatus';
import { FeedTape } from '@/views/ValueDashboard/FeedTape';
import { getFillAudioHealth, subscribeFillAudioHealth } from '@/lib/audio/fillCoordination';

/**
 * Global bottom status bar — mounted once at the foot of the content pane in
 * AuthShell, so it spans the content width (not the sidebar) on every screen.
 *
 * Fuses what used to be the Value Dashboard's local bottom bar (display toggles
 * + the live Hydra feed inspection) with the Phase 2 account cluster (key scope,
 * API tier, 429 pressure, manual upgrade). One strip, one place to glance at
 * "are we connected, what keys am I on, and are we hitting the rate-limit wall."
 */

const PRESSURE_BAR: Record<string, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
};
const PRESSURE_TEXT: Record<string, string> = {
  green: 'text-emerald-600 dark:text-emerald-400',
  yellow: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-600 dark:text-red-400',
};

// Graduated water-valve gauge. When the account is in 429 backoff RIGHT NOW we
// always show red — that state also catches an externally-cooked shared account
// (we get throttled even when our own load is low). Otherwise the fill is our
// outbound rate as a fraction of the tier's per-second budget — a real fuel
// gauge, not three states.
function pressureView(rl: RateLimitStatus | null): { status: string; widthPct: number; label: string } | null {
  if (!rl || rl.scope == null) return null;
  if (rl.throttled) {
    const secs = Math.ceil(rl.msUntilClear / 1000);
    return { status: 'red', widthPct: 92, label: secs > 0 ? `429 · ${secs}s` : 'throttled' };
  }
  const budget = rl.budgetReqPerSec && rl.budgetReqPerSec > 0 ? rl.budgetReqPerSec : 20;
  const rps = rl.reqPerSec ?? 0;
  const frac = Math.max(0, Math.min(1, rps / budget));
  const status = rl.rateLimitStrikes > 0 || frac >= 0.8 ? 'red' : frac >= 0.5 ? 'yellow' : 'green';
  return { status, widthPct: Math.max(4, Math.round(frac * 100)), label: `${rps}/s` };
}

// Feed-dot presentation per status. Quiet is a CALM state (muted dot, no
// warning color): hydra only sends frames when a market actually changes, so
// a thin/pregame slate is silent by design — see useFeedFreshness.
const FEED_DOT: Record<FeedStatus, string> = {
  disconnected: 'bg-negative',
  stalled: 'bg-negative',
  quiet: 'bg-muted-foreground/50',
  streaming: 'bg-positive animate-pulse',
};

/**
 * The status-bar half of the new-build prompt — the footer counterpart to the
 * rail pip. Same rules: it outlives Dismiss and stays until the tab reloads,
 * and clicking it re-opens the corner breakout rather than reloading, so a
 * stray click on a 11px strip can never restart a trading tab.
 */
function BuildUpdateItem() {
  const updateAvailable = useBuildUpdateStore((s) => s.updateAvailable);
  const showBreakout = useBuildUpdateStore((s) => s.showBreakout);
  if (!updateAvailable) return null;

  return (
    <>
      <button
        type="button"
        onClick={showBreakout}
        data-testid="build-update-status-item"
        title="A new version of g1 is available — click to review"
        className="flex items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-400 hover:bg-amber-500/25 light:text-amber-700"
      >
        <ArrowUpCircle className="h-3 w-3" />
        new version
      </button>
      <span className="bg-border h-4 w-px shrink-0" />
    </>
  );
}

/**
 * #843: this tab cannot coordinate fill sounds, so it plays none. Shown while a
 * reconnect to the sound coordinator gets no answer (clears if a later one
 * proves the same ledger) and, sticky until reload, after lost ledger history.
 * Never for one transient unconfirmed fill. Not clickable: a stray click on an
 * 11px strip must not reload a trading tab.
 */
export function FillSoundOffChip() {
  const health = useSyncExternalStore(subscribeFillAudioHealth, getFillAudioHealth);
  if (health === 'ok') return null;
  return (
    <span
      data-testid="fill-sound-off-chip"
      title="Fill sounds are off in this tab: it lost contact with the sound coordinator and can't confirm which fills already played. Reload the tab to turn them back on."
      className="flex shrink-0 items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-400 light:text-amber-700"
    >
      <VolumeX className="h-3 w-3" />
      sound off
    </span>
  );
}

/**
 * MrGreenMode's footer chip (2026-10-19). Shown ONLY while the mode is
 * on, so it is no clutter for everyone else; Settings → Dashboard and ladders
 * is where it is turned on. One click turns it off mid-game — no dialog: the
 * save is optimistic, so the chip goes at once and the full feed returns, and a
 * failed save rolls the mode back and the chip with it.
 */
export function LowBandwidthChip() {
  const on = useLowBandwidth();
  const setPrefs = useAuthStore((s) => s.setPrefs);
  if (!on) return null;
  return (
    <button
      type="button"
      onClick={() => { void setPrefs({ lowBandwidth: false }); }}
      data-testid="low-bandwidth-chip"
      title="MrGreenMode is on — optional game data loads on demand; click to turn off"
      className="flex shrink-0 items-center gap-1 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-400 hover:bg-sky-500/25 light:text-sky-700"
    >
      <WifiLow className="h-3 w-3" />
      MrGreenMode
    </button>
  );
}

export function StatusBar() {
  const { status: feedStatus, secondsSinceUpdate } = useFeedFreshness();
  const marketsCount = useMarketStore((s) => s.marketsCount);
  const orderbooksCount = useMarketStore((s) => s.orderbooksCount);
  const gh = useGatewayHealth();
  const tabBehind = gh != null && ((gh.queueLagMs ?? 0) > QUEUE_LAG_WARN_MS || (gh.flushP95Ms ?? 0) > FLUSH_P95_WARN_MS);
  // Read on render only (the bar re-renders on every health beat); null off-Chrome.
  const heapMb = jsHeapUsedMb();
  const rl = useRateLimitStatus();
  const role = useAuthStore((s) => s.user?.role);
  const [showFeedTape, setShowFeedTape] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const scopeLabel =
    rl?.scope === 'org' ? 'Org keys' : rl?.scope === 'user' ? 'Personal keys' : null;
  const p = pressureView(rl);
  const canUpgrade =
    (role === 'manager' || role === 'admin') && rl?.scope === 'org' && rl?.tier === 'basic';

  // Bulk upgrade via the existing per-credential endpoint: fetch the org's
  // Kalshi keys, fire the upgrade for each that isn't Advanced yet. No new relay
  // route needed; the tier readout refreshes off apiUsageUpgradedAt on the next
  // poll, which we kick immediately.
  const handleUpgrade = async () => {
    if (upgrading) return;
    setUpgrading(true);
    try {
      const res = await apiFetch('/api/manager/credentials?venue=kalshi');
      if (res.ok) {
        const { credentials } = (await res.json()) as {
          credentials?: Array<{ id: string; apiUsageUpgradedAt: string | null; disabledAt: string | null }>;
        };
        const pending = (credentials ?? []).filter((c) => !c.apiUsageUpgradedAt && !c.disabledAt);
        for (const c of pending) {
          await apiFetch(`/api/manager/credentials/${encodeURIComponent(c.id)}/upgrade-tier`, {
            method: 'POST',
          });
        }
      }
    } catch {
      // best-effort; the bar will keep showing basic if it didn't take
    } finally {
      setUpgrading(false);
      bumpRateLimitStatus();
    }
  };

  return (
    <div className="border-border bg-muted/20 flex items-center gap-3 border-t px-3 py-1.5 text-[11px]">
      <OddsFeeToggles />
      {/* #677 — beside the other app-wide display toggles, and writing the
          same canonical setting as Settings → Dashboard and ladders. */}
      <RestingOrderDisplayToggle />

      {/* Per-venue credential/connection dots — always on, quiet. A venue
          outage now degrades instead of blocking (Jul-9), so this is where
          "which venue is actually up" lives on every screen. */}
      <span className="bg-border h-4 w-px shrink-0" />
      <VenueCredStatus />

      {scopeLabel && (
        <>
          <span className="bg-border h-4 w-px shrink-0" />
          <span className="text-foreground flex items-center gap-1.5">
            <Key className="text-muted-foreground h-3 w-3" />
            {scopeLabel}
            {rl && rl.keyCount > 0 && (
              <span className="text-muted-foreground">
                · {rl.keyCount} {rl.keyCount === 1 ? 'key' : 'keys'}
              </span>
            )}
          </span>

          {rl && rl.tier !== 'unknown' && (
            <span
              className={
                rl.tier === 'advanced'
                  ? 'rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-600 dark:text-emerald-400'
                  : 'rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-600 dark:text-amber-400'
              }
            >
              {rl.tier}
            </span>
          )}

          {p && (
            <span className="text-muted-foreground flex items-center gap-1.5" title="Kalshi rate-limit pressure (429 backoff)">
              {rl?.scope === 'org' ? 'org' : 'pers'}
              <span className="bg-muted/30 relative h-1.5 w-16 overflow-hidden rounded">
                <span className={`${PRESSURE_BAR[p.status]} block h-full`} style={{ width: `${p.widthPct}%` }} />
              </span>
              <span className={`tabular-nums ${PRESSURE_TEXT[p.status]}`}>{p.label}</span>
            </span>
          )}

          {canUpgrade && (
            <button
              onClick={handleUpgrade}
              disabled={upgrading}
              title="Upgrade this org's Kalshi keys to the Advanced API tier (3× write budget)"
              className="text-primary hover:bg-accent flex items-center gap-1 rounded px-1.5 py-0.5 transition-colors disabled:opacity-50"
            >
              <ArrowUpCircle className="h-3 w-3" />
              {upgrading ? 'upgrading…' : 'upgrade'}
            </button>
          )}
        </>
      )}

      <div className="relative ml-auto flex items-center gap-2">
        <RelayRestartControl />
        <BuildUpdateItem />
        <FillSoundOffChip />
        <OrderbooksPausedBadge />
        <LowBandwidthChip />
        <button
          onClick={() => setShowFeedTape((v) => !v)}
          className="hover:bg-accent flex min-w-44 items-center gap-2 rounded-md px-2 py-1 transition-colors"
          title={
            feedStatus === 'disconnected'
              ? 'Hydra WS disconnected'
              : feedStatus === 'stalled'
                ? `Hydra WS silent for ${secondsSinceUpdate ?? '?'}s (no frames, no keepalives) — click anything to refresh`
                : feedStatus === 'quiet'
                  ? `Hydra connected — no market changes in ${secondsSinceUpdate ?? '?'}s (normal on a quiet slate)`
                  : gh
                    ? `Gateway streaming · ${gh.framesPerSec} frames/s · queue lag ${gh.queueLagMs ?? '?'} ms (time a beat waited behind frames this tab had not processed) · store flush p95 ${gh.flushP95Ms ?? '?'} ms (cap 33) · rtt ${gh.rttMs ?? '?'} ms${heapMb != null ? ` · tab heap ${heapMb} MB` : ''}`
                    : 'Hydra streaming'
          }
        >
          <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${FEED_DOT[feedStatus]}`} />
          <span className="text-muted-foreground truncate">
            {feedStatus === 'disconnected'
              ? 'Disconnected'
              : feedStatus === 'stalled'
                ? `Stalled (${secondsSinceUpdate ?? '?'}s)`
                : feedStatus === 'quiet'
                  ? `Quiet · ${marketsCount} mkts`
                  : `${marketsCount} mkts / ${orderbooksCount} books`}
          </span>
          {gh && feedStatus === 'streaming' && (
            <span className={`shrink-0 tabular-nums ${tabBehind ? 'text-amber-400' : 'text-muted-foreground/70'}`}>
              {gh.framesPerSec}/s · lag {gh.queueLagMs ?? '–'}
            </span>
          )}
        </button>
        {showFeedTape && <FeedTape onClose={() => setShowFeedTape(false)} />}
      </div>
    </div>
  );
}
